import { LiksConfirmDialog } from '../../components/liks/LiksConfirmDialog/LiksConfirmDialog';
import {
  useAssignTenantUserLabelMutation,
  useCreateTenantLabelMutation,
  useDeleteTenantLabelMutation,
  useRenameTenantLabelMutation,
  useUnassignTenantUserLabelMutation,
} from '../../features/tenant/mutations/useTenantLabelMutations';
import { useI18n } from '../../i18n/useI18n';
import { toastMutationError } from '../../query/mutationToasts';
import { toast } from '../../toast/toast';
import type { AssignedDirectoryUser } from '../../types/user';
import type { WithResourceId } from '../../types/knowledge';
import type { UserLabel } from '../../types/tenant';
import { TenantNamedResourceDialog } from './TenantNamedResourceDialog';
import type { TenantAssignableUser } from './TenantResourceUserAssignments';
import { TenantResourceUsersDialog } from './TenantResourceUsersDialog';

type NamedLabel = WithResourceId<UserLabel> & { name: string };

type TenantLabelsSectionDialogsProps = {
  isCreateOpen: boolean;
  onCloseCreate: () => void;
  renameTarget: NamedLabel | null;
  onCloseRename: () => void;
  deleteTarget: NamedLabel | null;
  onCloseDelete: () => void;
  manageTarget: NamedLabel | null;
  onCloseManage: () => void;
  directoryUsers: ReadonlyArray<TenantAssignableUser>;
  assignedUsers: ReadonlyArray<AssignedDirectoryUser>;
  isManageLoading: boolean;
  isManageError: boolean;
  pendingUserId: string | null;
  setPendingUserId: (userId: string | null) => void;
  createMutation: ReturnType<typeof useCreateTenantLabelMutation>;
  renameMutation: ReturnType<typeof useRenameTenantLabelMutation>;
  deleteMutation: ReturnType<typeof useDeleteTenantLabelMutation>;
  assignMutation: ReturnType<typeof useAssignTenantUserLabelMutation>;
  unassignMutation: ReturnType<typeof useUnassignTenantUserLabelMutation>;
};

export function TenantLabelsSectionDialogs({
  isCreateOpen,
  onCloseCreate,
  renameTarget,
  onCloseRename,
  deleteTarget,
  onCloseDelete,
  manageTarget,
  onCloseManage,
  directoryUsers,
  assignedUsers,
  isManageLoading,
  isManageError,
  pendingUserId,
  setPendingUserId,
  createMutation,
  renameMutation,
  deleteMutation,
  assignMutation,
  unassignMutation,
}: TenantLabelsSectionDialogsProps) {
  const { translate } = useI18n();

  return (
    <>
      {isCreateOpen ? (
        <TenantNamedResourceDialog
          title={translate(
            'organisationManagement.labelsAndGroups.createLabelTitle',
          )}
          nameLabel={translate(
            'organisationManagement.labelsAndGroups.nameLabel',
          )}
          namePlaceholder={translate(
            'organisationManagement.labelsAndGroups.labelNamePlaceholder',
          )}
          submitLabel={translate(
            'organisationManagement.labelsAndGroups.createLabel',
          )}
          cancelLabel={translate(
            'organisationManagement.labelsAndGroups.cancel',
          )}
          closeLabel={translate('organisationManagement.labelsAndGroups.close')}
          isSubmitting={createMutation.isPending}
          onClose={onCloseCreate}
          onSubmit={(name) => {
            createMutation.mutate(name, {
              onSuccess: () => {
                toast.success({
                  title: translate(
                    'organisationManagement.labelsAndGroups.createLabelSuccessTitle',
                  ),
                  description: '',
                });
                onCloseCreate();
              },
              onError: (error) => {
                void toastMutationError(
                  error,
                  translate(
                    'organisationManagement.labelsAndGroups.createLabelErrorTitle',
                  ),
                );
              },
            });
          }}
        />
      ) : null}

      {renameTarget ? (
        <TenantNamedResourceDialog
          title={translate(
            'organisationManagement.labelsAndGroups.renameLabelTitle',
          )}
          nameLabel={translate(
            'organisationManagement.labelsAndGroups.nameLabel',
          )}
          namePlaceholder={translate(
            'organisationManagement.labelsAndGroups.labelNamePlaceholder',
          )}
          submitLabel={translate(
            'organisationManagement.labelsAndGroups.saveRename',
          )}
          cancelLabel={translate(
            'organisationManagement.labelsAndGroups.cancel',
          )}
          closeLabel={translate('organisationManagement.labelsAndGroups.close')}
          initialName={renameTarget.name}
          isSubmitting={renameMutation.isPending}
          onClose={onCloseRename}
          onSubmit={(name) => {
            renameMutation.mutate(
              { labelId: renameTarget.id, name },
              {
                onSuccess: () => {
                  toast.success({
                    title: translate(
                      'organisationManagement.labelsAndGroups.renameLabelSuccessTitle',
                    ),
                    description: '',
                  });
                  onCloseRename();
                },
                onError: (error) => {
                  void toastMutationError(
                    error,
                    translate(
                      'organisationManagement.labelsAndGroups.renameLabelErrorTitle',
                    ),
                  );
                },
              },
            );
          }}
        />
      ) : null}

      {manageTarget ? (
        <TenantResourceUsersDialog
          title={translate(
            'organisationManagement.labelsAndGroups.manageUsersTitle',
            { name: manageTarget.name },
          )}
          closeLabel={translate('organisationManagement.labelsAndGroups.close')}
          assignedUsers={assignedUsers}
          directoryUsers={directoryUsers}
          isLoading={isManageLoading}
          isError={isManageError}
          pendingUserId={pendingUserId}
          isBusy={assignMutation.isPending || unassignMutation.isPending}
          onClose={() => {
            if (!assignMutation.isPending && !unassignMutation.isPending) {
              onCloseManage();
              setPendingUserId(null);
            }
          }}
          onAssign={(userId) => {
            setPendingUserId(userId);
            assignMutation.mutate(
              { userId, labelId: manageTarget.id },
              {
                onSuccess: () => {
                  toast.success({
                    title: translate(
                      'organisationManagement.labelsAndGroups.assignLabelSuccessTitle',
                    ),
                    description: '',
                  });
                },
                onError: (error) => {
                  void toastMutationError(
                    error,
                    translate(
                      'organisationManagement.labelsAndGroups.assignLabelErrorTitle',
                    ),
                  );
                },
                onSettled: () => setPendingUserId(null),
              },
            );
          }}
          onUnassign={(userId) => {
            setPendingUserId(userId);
            unassignMutation.mutate(
              { userId, labelId: manageTarget.id },
              {
                onSuccess: () => {
                  toast.success({
                    title: translate(
                      'organisationManagement.labelsAndGroups.unassignLabelSuccessTitle',
                    ),
                    description: '',
                  });
                },
                onError: (error) => {
                  void toastMutationError(
                    error,
                    translate(
                      'organisationManagement.labelsAndGroups.unassignLabelErrorTitle',
                    ),
                  );
                },
                onSettled: () => setPendingUserId(null),
              },
            );
          }}
        />
      ) : null}

      <LiksConfirmDialog
        isOpen={deleteTarget != null}
        title={translate(
          'organisationManagement.labelsAndGroups.deleteLabelTitle',
        )}
        message={translate(
          'organisationManagement.labelsAndGroups.deleteLabelMessage',
          { name: deleteTarget?.name ?? '' },
        )}
        confirmLabel={translate(
          'organisationManagement.labelsAndGroups.delete',
        )}
        cancelLabel={translate('organisationManagement.labelsAndGroups.cancel')}
        onCancel={() => {
          if (!deleteMutation.isPending) {
            onCloseDelete();
          }
        }}
        onConfirm={() => {
          if (!deleteTarget || deleteMutation.isPending) {
            return;
          }
          deleteMutation.mutate(deleteTarget.id, {
            onSuccess: () => {
              toast.success({
                title: translate(
                  'organisationManagement.labelsAndGroups.deleteLabelSuccessTitle',
                ),
                description: '',
              });
              onCloseDelete();
            },
            onError: (error) => {
              void toastMutationError(
                error,
                translate(
                  'organisationManagement.labelsAndGroups.deleteLabelErrorTitle',
                ),
              );
            },
          });
        }}
      />
    </>
  );
}
